import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';
import { supabase } from '../../src/lib/supabase';
import { logUsageEvent } from '../../src/lib/logger';
import { AvisoConsumidor, AvisoConsumidorConfirmacao } from '../../types';
import LoadingSpinner from '../../src/components/LoadingSpinner';
import ConfirmationModal from '../../src/components/ConfirmationModal';
import { Megaphone, Plus, X, Loader2, Pencil, Archive, ChevronDown, ChevronRight, ExternalLink, Upload, EyeOff, Bold, Italic, List, Eye } from 'lucide-react';
import MensagemAvisoMarkdown from '../../src/components/MensagemAvisoMarkdown';
import { envolverSelecao, alternarListaSelecao, ResultadoFormatacao } from '../../src/lib/formatarMensagemAviso';

// Painel de gestão de avisos/comunicados do Portal do Consumidor (nova aba de
// Configurações). Leitura para adm/gerente/analista (ver ConfigView.tsx); criar,
// editar e publicar é só adm — reforçado pela RLS em
// 20260824131000_avisos_consumidor_rls.sql, não só pela UI.
//
// Editar um aviso já publicado nunca faz UPDATE no texto: cria uma nova linha
// (versao_anterior_id aponta pra anterior) e arquiva a antiga — ver comentário em
// src/lib/resolverAvisosPendentes.ts.
//
// Tirar do ar tem dois caminhos, de propósito:
//   • Despublicar (publicado → rascunho): tira da frente do cliente e devolve o aviso
//     para edição, mantendo o MESMO id. Quem já confirmou continua confirmado e não
//     vê o aviso de novo quando ele voltar ao ar. É o caminho para "publiquei errado".
//   • Arquivar: encerra o aviso de vez, sem volta pela UI. É o caminho para "esse
//     comunicado acabou".

type ConfirmacaoComPerfil = AvisoConsumidorConfirmacao & { profiles: { full_name: string } | null };

const STATUS_CONFIG: Record<AvisoConsumidor['status'], { label: string; color: string }> = {
    rascunho: { label: 'Rascunho', color: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400' },
    publicado: { label: 'Publicado', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
    arquivado: { label: 'Arquivado', color: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500' },
};

const CATEGORIAS_SUGERIDAS = ['contrato', 'novidade', 'aceite'];
const OPCAO_CATEGORIA_OUTRA = '__outra__';
const BUCKET_ANEXOS = 'avisos-anexos';
const TAMANHO_MAXIMO_ANEXO = 10 * 1024 * 1024; // 10 MB

const dataHoraBr = (iso?: string | null): string => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const paraDatetimeLocal = (iso?: string | null): string => {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

interface FormAviso {
    titulo: string;
    categoria: string;
    mensagem: string;
    bloqueante: boolean;
    url_anexo: string;
    data_inicio: string;
    data_fim: string;
    publico: string;
}

// Público do aviso no portal (#6117): cada marca só vê o que é dela. 'vigor' é o padrão —
// é o que valia antes de existir a Gran, e o convite do boleto único não pode ir para ela.
const PUBLICOS: { valor: string; rotulo: string }[] = [
    { valor: 'vigor', rotulo: 'Clientes Vigor' },
    { valor: 'gran', rotulo: 'Clientes Gran' },
    { valor: 'todos', rotulo: 'Todos os clientes' },
];
const rotuloPublico = (v?: string | null) => PUBLICOS.find((p) => p.valor === (v || 'vigor'))?.rotulo ?? v ?? 'Clientes Vigor';

const FORM_VAZIO: FormAviso = { titulo: '', categoria: '', mensagem: '', bloqueante: false, url_anexo: '', data_inicio: '', data_fim: '', publico: 'vigor' };

const AvisosConsumidorView: React.FC = () => {
    const { profile } = useAuth();
    const { addToast } = useToast();
    const souAdm = profile?.role === 'adm';

    const [avisos, setAvisos] = useState<AvisoConsumidor[]>([]);
    const [confirmacoes, setConfirmacoes] = useState<AvisoConsumidorConfirmacao[]>([]);
    const [totalConsumidores, setTotalConsumidores] = useState(0);
    const [loading, setLoading] = useState(true);

    const [expandidoId, setExpandidoId] = useState<string | null>(null);
    const [detalhes, setDetalhes] = useState<Map<string, ConfirmacaoComPerfil[]>>(new Map());
    const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);

    const [modalAberto, setModalAberto] = useState(false);
    const [editando, setEditando] = useState<AvisoConsumidor | null>(null);
    const [form, setForm] = useState<FormAviso>(FORM_VAZIO);
    const [categoriaModo, setCategoriaModo] = useState<'lista' | 'outra'>('lista');
    const [salvando, setSalvando] = useState(false);
    const [enviandoAnexo, setEnviandoAnexo] = useState(false);

    const [confirmarPublicar, setConfirmarPublicar] = useState<{ id?: string } | null>(null);
    const [confirmarArquivar, setConfirmarArquivar] = useState<AvisoConsumidor | null>(null);
    const [confirmarDespublicar, setConfirmarDespublicar] = useState<AvisoConsumidor | null>(null);
    const [previaMensagem, setPreviaMensagem] = useState(false);
    const mensagemRef = React.useRef<HTMLTextAreaElement>(null);

    const carregar = async () => {
        setLoading(true);
        try {
            const [{ data: avisosData, error: errAvisos }, { data: confirmData, error: errConfirm }, { count }] = await Promise.all([
                supabase.from('avisos_consumidor').select('*, profiles(full_name)').order('criado_em', { ascending: false }),
                supabase.from('avisos_consumidor_confirmacoes').select('id, aviso_id, profile_id, visualizado_em, confirmado_em'),
                supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('role', 'consumidor'),
            ]);
            if (errAvisos) throw errAvisos;
            if (errConfirm) throw errConfirm;
            setAvisos((avisosData || []) as AvisoConsumidor[]);
            setConfirmacoes((confirmData || []) as AvisoConsumidorConfirmacao[]);
            setTotalConsumidores(count || 0);
        } catch (err) {
            console.error('Erro ao carregar avisos do portal:', err);
            addToast('Erro ao carregar avisos.', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { carregar(); }, []);

    const statsPorAviso = useMemo(() => {
        const mapa = new Map<string, { visualizaram: number; confirmaram: number }>();
        confirmacoes.forEach((c) => {
            const atual = mapa.get(c.aviso_id) || { visualizaram: 0, confirmaram: 0 };
            if (c.visualizado_em) atual.visualizaram += 1;
            if (c.confirmado_em) atual.confirmaram += 1;
            mapa.set(c.aviso_id, atual);
        });
        return mapa;
    }, [confirmacoes]);

    const abrirNovo = () => {
        setEditando(null);
        setForm(FORM_VAZIO);
        setCategoriaModo('lista');
        setPreviaMensagem(false);
        setModalAberto(true);
    };

    const abrirEdicao = (aviso: AvisoConsumidor) => {
        setEditando(aviso);
        setForm({
            titulo: aviso.titulo,
            categoria: aviso.categoria,
            mensagem: aviso.mensagem,
            bloqueante: aviso.bloqueante,
            url_anexo: aviso.url_anexo || '',
            data_inicio: paraDatetimeLocal(aviso.data_inicio),
            data_fim: paraDatetimeLocal(aviso.data_fim),
            publico: aviso.publico || 'vigor',
        });
        setCategoriaModo(CATEGORIAS_SUGERIDAS.includes(aviso.categoria) ? 'lista' : 'outra');
        setPreviaMensagem(false);
        setModalAberto(true);
    };

    const enviarAnexo = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // permite escolher o mesmo arquivo de novo depois
        if (!file) return;
        if (file.size > TAMANHO_MAXIMO_ANEXO) {
            addToast('Arquivo muito grande (máximo 10 MB).', 'error');
            return;
        }
        setEnviandoAnexo(true);
        try {
            const path = `${crypto.randomUUID()}-${file.name}`;
            const { error: upErr } = await supabase.storage.from(BUCKET_ANEXOS).upload(path, file);
            if (upErr) throw upErr;
            const { data } = supabase.storage.from(BUCKET_ANEXOS).getPublicUrl(path);
            setForm((prev) => ({ ...prev, url_anexo: data.publicUrl }));
            addToast('Arquivo enviado.', 'success');
        } catch (err) {
            console.error('Erro ao enviar anexo:', err);
            addToast('Erro ao enviar o arquivo.', 'error');
        } finally {
            setEnviandoAnexo(false);
        }
    };

    const payloadDoForm = () => ({
        titulo: form.titulo.trim(),
        categoria: form.categoria.trim(),
        mensagem: form.mensagem.trim(),
        bloqueante: form.bloqueante,
        url_anexo: form.url_anexo.trim() || null,
        data_inicio: form.data_inicio ? new Date(form.data_inicio).toISOString() : null,
        data_fim: form.data_fim ? new Date(form.data_fim).toISOString() : null,
        publico: form.publico || 'vigor',
    });

    // Usado tanto dentro de salvar() quanto no botão "Publicar" do modal — precisa
    // barrar ANTES de abrir o ConfirmationModal, senão o usuário confirma a
    // publicação e só então descobre (com o modal de confirmação já fechado) que
    // faltava um campo obrigatório.
    const formValido = (): boolean => {
        if (!form.titulo.trim() || !form.categoria.trim() || !form.mensagem.trim()) {
            addToast('Preencha título, categoria e mensagem.', 'error');
            return false;
        }
        return true;
    };

    const salvar = async (status: 'rascunho' | 'publicado') => {
        if (!formValido()) return;
        setSalvando(true);
        try {
            const payload = payloadDoForm();

            if (!editando) {
                const { error } = await supabase.from('avisos_consumidor').insert({ ...payload, status, criado_por: profile!.id });
                if (error) throw error;
            } else if (editando.status === 'rascunho') {
                const { error } = await supabase.from('avisos_consumidor').update({ ...payload, status }).eq('id', editando.id);
                if (error) throw error;
            } else {
                // Aviso já publicado: nunca faz UPDATE no texto — cria nova versão e
                // arquiva a antiga (ver comentário no topo do arquivo).
                const { error: errInsert } = await supabase
                    .from('avisos_consumidor')
                    .insert({ ...payload, status, criado_por: profile!.id, versao_anterior_id: editando.id });
                if (errInsert) throw errInsert;
                const { error: errArquivar } = await supabase
                    .from('avisos_consumidor')
                    .update({ status: 'arquivado' })
                    .eq('id', editando.id);
                if (errArquivar) throw errArquivar;
            }

            addToast(status === 'publicado' ? 'Aviso publicado.' : 'Rascunho salvo.', 'success');
            logUsageEvent({ userId: profile?.id, modulo: 'config', funcionalidade: 'avisos', acao: 'salvar_aviso_consumidor', origem: 'acao' });
            setModalAberto(false);
            await carregar();
        } catch (err) {
            console.error('Erro ao salvar aviso:', err);
            addToast('Erro ao salvar o aviso.', 'error');
        } finally {
            setSalvando(false);
            setConfirmarPublicar(null);
        }
    };

    const publicarRascunho = async (id: string) => {
        try {
            const { error } = await supabase.from('avisos_consumidor').update({ status: 'publicado' }).eq('id', id);
            if (error) throw error;
            addToast('Aviso publicado.', 'success');
            logUsageEvent({ userId: profile?.id, modulo: 'config', funcionalidade: 'avisos', acao: 'publicar_aviso_consumidor', origem: 'acao' });
            await carregar();
        } catch (err) {
            console.error('Erro ao publicar aviso:', err);
            addToast('Erro ao publicar o aviso.', 'error');
        } finally {
            setConfirmarPublicar(null);
        }
    };

    // publicado → rascunho. Mantém o mesmo id (não cria versão nova como a edição de
    // um publicado faz): o aviso some do portal na hora e volta editável, e quem já
    // confirmou segue confirmado quando ele for publicado de novo.
    const despublicar = async (aviso: AvisoConsumidor) => {
        try {
            const { error } = await supabase.from('avisos_consumidor').update({ status: 'rascunho' }).eq('id', aviso.id);
            if (error) throw error;
            addToast('Aviso despublicado. Ele voltou para rascunho.', 'success');
            logUsageEvent({ userId: profile?.id, modulo: 'config', funcionalidade: 'avisos', acao: 'despublicar_aviso_consumidor', origem: 'acao' });
            await carregar();
        } catch (err) {
            console.error('Erro ao despublicar aviso:', err);
            addToast('Erro ao despublicar o aviso.', 'error');
        } finally {
            setConfirmarDespublicar(null);
        }
    };

    const arquivar = async (aviso: AvisoConsumidor) => {
        try {
            const { error } = await supabase.from('avisos_consumidor').update({ status: 'arquivado' }).eq('id', aviso.id);
            if (error) throw error;
            addToast('Aviso arquivado.', 'success');
            logUsageEvent({ userId: profile?.id, modulo: 'config', funcionalidade: 'avisos', acao: 'arquivar_aviso_consumidor', origem: 'acao' });
            await carregar();
        } catch (err) {
            console.error('Erro ao arquivar aviso:', err);
            addToast('Erro ao arquivar o aviso.', 'error');
        } finally {
            setConfirmarArquivar(null);
        }
    };

    // Aplica o resultado de uma das funções puras de formatarMensagemAviso.ts no
    // textarea e devolve o cursor para dentro do trecho marcado. O
    // requestAnimationFrame importa: o React re-renderiza a textarea com o value novo
    // DEPOIS deste handler, e sem esperar o próximo frame o setSelectionRange é
    // desfeito pela re-renderização e o cursor pula para o fim do texto.
    const aplicarNaMensagem = (calcular: (texto: string, inicio: number, fim: number) => ResultadoFormatacao) => {
        const el = mensagemRef.current;
        if (!el) return;
        const r = calcular(form.mensagem, el.selectionStart, el.selectionEnd);
        setForm({ ...form, mensagem: r.texto });
        requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(r.selecaoInicio, r.selecaoFim);
        });
    };

    const aplicarFormato = (marca: string, exemplo: string) =>
        aplicarNaMensagem((texto, ini, fim) => envolverSelecao(texto, ini, fim, marca, exemplo));

    const alternarLista = () => aplicarNaMensagem(alternarListaSelecao);

    // Ctrl/Cmd+B e Ctrl/Cmd+I: sem isso o negrito só sai pelo botão, e quem escreve
    // um comunicado longo digita o atalho por reflexo (e o navegador não faz nada).
    const atalhosMensagem = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (!(e.ctrlKey || e.metaKey)) return;
        const tecla = e.key.toLowerCase();
        if (tecla === 'b') { e.preventDefault(); aplicarFormato('**', 'texto em negrito'); }
        if (tecla === 'i') { e.preventDefault(); aplicarFormato('*', 'texto em itálico'); }
    };

    const alternarDetalhe = async (avisoId: string) => {
        if (expandidoId === avisoId) {
            setExpandidoId(null);
            return;
        }
        setExpandidoId(avisoId);
        if (detalhes.has(avisoId)) return;
        setCarregandoDetalhe(true);
        try {
            const { data, error } = await supabase
                .from('avisos_consumidor_confirmacoes')
                .select('id, aviso_id, profile_id, visualizado_em, confirmado_em, profiles(full_name)')
                .eq('aviso_id', avisoId)
                .order('visualizado_em', { ascending: false });
            if (error) throw error;
            setDetalhes((prev) => new Map(prev).set(avisoId, (data || []) as unknown as ConfirmacaoComPerfil[]));
        } catch (err) {
            console.error('Erro ao carregar confirmações do aviso:', err);
            addToast('Erro ao carregar quem visualizou/confirmou.', 'error');
        } finally {
            setCarregandoDetalhe(false);
        }
    };

    if (loading) return <LoadingSpinner label="Carregando avisos..." />;

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <Megaphone size={22} className="text-primary" />
                        Avisos ao Consumidor
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Comunicados exibidos no Portal do Consumidor, com confirmação de leitura/aceite.
                    </p>
                </div>
                {souAdm && (
                    <button
                        onClick={abrirNovo}
                        className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-sm px-4 py-2.5 rounded-xl transition-colors"
                    >
                        <Plus size={16} />
                        Novo aviso
                    </button>
                )}
            </div>

            {avisos.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-12 text-center">
                    <Megaphone size={40} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                    <p className="text-gray-500 dark:text-gray-400">Nenhum aviso criado ainda.</p>
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-500 dark:text-gray-300 text-xs font-bold uppercase tracking-wider">
                                <tr>
                                    <th className="px-6 py-4">Aviso</th>
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4">Bloqueante</th>
                                    <th className="px-6 py-4">Vigência</th>
                                    <th className="px-6 py-4">Confirmações</th>
                                    <th className="px-6 py-4 text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {avisos.map((aviso) => {
                                    const stats = statsPorAviso.get(aviso.id) || { visualizaram: 0, confirmaram: 0 };
                                    const progresso = totalConsumidores > 0 ? Math.round((stats.confirmaram / totalConsumidores) * 100) : 0;
                                    const expandido = expandidoId === aviso.id;
                                    return (
                                        <React.Fragment key={aviso.id}>
                                            <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                                <td className="px-6 py-4 max-w-xs">
                                                    <button onClick={() => alternarDetalhe(aviso.id)} className="flex items-start gap-2 text-left">
                                                        {expandido ? <ChevronDown size={16} className="mt-0.5 shrink-0 text-gray-400" /> : <ChevronRight size={16} className="mt-0.5 shrink-0 text-gray-400" />}
                                                        <div>
                                                            <p className="font-semibold text-gray-900 dark:text-white truncate">{aviso.titulo}</p>
                                                            <p className="text-xs text-gray-400 truncate">{aviso.categoria} · {rotuloPublico(aviso.publico)}</p>
                                                            <p className="text-[11px] text-gray-400 truncate">
                                                                por {(aviso as unknown as { profiles: { full_name: string } | null }).profiles?.full_name || '—'}
                                                            </p>
                                                        </div>
                                                    </button>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`inline-flex text-xs font-bold px-2.5 py-1 rounded-full ${STATUS_CONFIG[aviso.status].color}`}>
                                                        {STATUS_CONFIG[aviso.status].label}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                                                    {aviso.bloqueante ? 'Sim' : 'Não'}
                                                </td>
                                                <td className="px-6 py-4 text-xs text-gray-500 dark:text-gray-400">
                                                    <p>De: {dataHoraBr(aviso.data_inicio)}</p>
                                                    <p>Até: {dataHoraBr(aviso.data_fim)}</p>
                                                </td>
                                                <td className="px-6 py-4 min-w-[160px]">
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                                                            <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${progresso}%` }} />
                                                        </div>
                                                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 shrink-0">
                                                            {stats.confirmaram}/{totalConsumidores}
                                                        </span>
                                                    </div>
                                                    <p className="text-[11px] text-gray-400 mt-1">{stats.visualizaram} visualizaram</p>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    {souAdm && (
                                                        <div className="flex justify-end gap-2">
                                                            {aviso.status !== 'arquivado' && (
                                                                <button onClick={() => abrirEdicao(aviso)} title="Editar" className="p-2 rounded-lg text-gray-400 hover:text-primary hover:bg-primary/10 transition-colors">
                                                                    <Pencil size={16} />
                                                                </button>
                                                            )}
                                                            {aviso.status === 'rascunho' && (
                                                                <button onClick={() => setConfirmarPublicar({ id: aviso.id })} title="Publicar" className="p-2 rounded-lg text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors">
                                                                    <Megaphone size={16} />
                                                                </button>
                                                            )}
                                                            {aviso.status === 'publicado' && (
                                                                <button onClick={() => setConfirmarDespublicar(aviso)} title="Despublicar (volta para rascunho)" className="p-2 rounded-lg text-gray-400 hover:text-secondary-dark hover:bg-secondary/10 transition-colors">
                                                                    <EyeOff size={16} />
                                                                </button>
                                                            )}
                                                            {aviso.status !== 'arquivado' && (
                                                                // Rascunho também arquiva: sem isso, um aviso despublicado (ou um
                                                                // rascunho abandonado) fica preso na lista para sempre, porque o
                                                                // único caminho para 'arquivado' seria publicá-lo de novo antes.
                                                                <button onClick={() => setConfirmarArquivar(aviso)} title="Arquivar" className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                                                                    <Archive size={16} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                            {expandido && (
                                                <tr>
                                                    <td colSpan={6} className="px-6 py-4 bg-gray-50 dark:bg-gray-900/30">
                                                        <div className="text-sm mb-3">
                                                            <MensagemAvisoMarkdown value={aviso.mensagem} variante="admin" />
                                                        </div>
                                                        {aviso.url_anexo && (
                                                            <a href={aviso.url_anexo} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary mb-4">
                                                                Ver anexo <ExternalLink size={12} />
                                                            </a>
                                                        )}
                                                        {carregandoDetalhe && !detalhes.has(aviso.id) ? (
                                                            <p className="text-xs text-gray-400 flex items-center gap-1.5">
                                                                <Loader2 size={12} className="animate-spin" />
                                                                Carregando confirmações...
                                                            </p>
                                                        ) : (
                                                            <div className="max-h-56 overflow-y-auto">
                                                                <table className="w-full text-left text-xs">
                                                                    <thead className="text-gray-400 uppercase tracking-wide">
                                                                        <tr>
                                                                            <th className="py-1.5 pr-4">Consumidor</th>
                                                                            <th className="py-1.5 pr-4">Visualizou em</th>
                                                                            <th className="py-1.5">Confirmou em</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                                                        {(detalhes.get(aviso.id) || []).map((c) => (
                                                                            <tr key={c.id}>
                                                                                <td className="py-1.5 pr-4 text-gray-700 dark:text-gray-300">{c.profiles?.full_name || c.profile_id}</td>
                                                                                <td className="py-1.5 pr-4 text-gray-500">{dataHoraBr(c.visualizado_em)}</td>
                                                                                <td className="py-1.5 text-gray-500">{dataHoraBr(c.confirmado_em)}</td>
                                                                            </tr>
                                                                        ))}
                                                                        {(detalhes.get(aviso.id) || []).length === 0 && !carregandoDetalhe && (
                                                                            <tr><td colSpan={3} className="py-2 text-gray-400">Ninguém visualizou este aviso ainda.</td></tr>
                                                                        )}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {modalAberto && ReactDOM.createPortal(
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
                        <div className="px-6 pt-6 pb-4 flex items-start justify-between">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                {editando ? 'Editar aviso' : 'Novo aviso'}
                            </h3>
                            <button onClick={() => setModalAberto(false)} className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="px-6 pb-6 space-y-4">
                            {editando && editando.status === 'publicado' && (
                                <p className="text-xs bg-secondary/10 text-secondary-dark border border-secondary/20 rounded-lg px-3 py-2">
                                    Este aviso já foi publicado. Salvar aqui cria uma nova versão e arquiva a atual — consumidores que já confirmaram vão ver a nova versão como pendente de novo. Para só corrigir sem avisar todo mundo de novo, feche, despublique o aviso na lista e edite o rascunho.
                                </p>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Título</label>
                                <input
                                    value={form.titulo}
                                    onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-900 dark:text-white rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Categoria</label>
                                <select
                                    value={categoriaModo === 'outra' ? OPCAO_CATEGORIA_OUTRA : form.categoria}
                                    onChange={(e) => {
                                        if (e.target.value === OPCAO_CATEGORIA_OUTRA) {
                                            setCategoriaModo('outra');
                                            setForm({ ...form, categoria: '' });
                                        } else {
                                            setCategoriaModo('lista');
                                            setForm({ ...form, categoria: e.target.value });
                                        }
                                    }}
                                    className="w-full p-2.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                                >
                                    <option value="" disabled>Selecione uma categoria</option>
                                    {CATEGORIAS_SUGERIDAS.map((c) => (
                                        <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                                    ))}
                                    <option value={OPCAO_CATEGORIA_OUTRA}>Outra (personalizada)</option>
                                </select>
                                {categoriaModo === 'outra' && (
                                    <input
                                        value={form.categoria}
                                        onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                                        placeholder="Digite a categoria"
                                        className="mt-2 w-full px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-900 dark:text-white rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                    />
                                )}
                            </div>
                            <div>
                                <div className="flex items-center justify-between mb-1.5">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Mensagem</label>
                                    <button
                                        type="button"
                                        onClick={() => setPreviaMensagem((v) => !v)}
                                        className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-primary transition-colors"
                                    >
                                        <Eye size={13} />
                                        {previaMensagem ? 'Voltar a editar' : 'Ver como o cliente vê'}
                                    </button>
                                </div>
                                {previaMensagem ? (
                                    // Mesma renderização do Portal do Consumidor (MensagemAvisoMarkdown):
                                    // é a prévia real do aviso, não uma aproximação.
                                    <div className="min-h-[130px] px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/50 text-sm">
                                        {form.mensagem.trim()
                                            ? <MensagemAvisoMarkdown value={form.mensagem} variante="admin" />
                                            : <p className="text-gray-400">Nada escrito ainda.</p>}
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex items-center gap-1 mb-1.5">
                                            <button type="button" onClick={() => aplicarFormato('**', 'texto em negrito')} title="Negrito (Ctrl+B)" className="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:text-primary hover:bg-primary/10 transition-colors">
                                                <Bold size={15} />
                                            </button>
                                            <button type="button" onClick={() => aplicarFormato('*', 'texto em itálico')} title="Itálico (Ctrl+I)" className="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:text-primary hover:bg-primary/10 transition-colors">
                                                <Italic size={15} />
                                            </button>
                                            <button type="button" onClick={alternarLista} title="Lista com marcadores" className="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:text-primary hover:bg-primary/10 transition-colors">
                                                <List size={15} />
                                            </button>
                                            <span className="text-[11px] text-gray-400 ml-1">Selecione um trecho e clique para formatar</span>
                                        </div>
                                        <textarea
                                            ref={mensagemRef}
                                            value={form.mensagem}
                                            onChange={(e) => setForm({ ...form, mensagem: e.target.value })}
                                            onKeyDown={atalhosMensagem}
                                            rows={6}
                                            className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-900 dark:text-white rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                        />
                                        <p className="text-[11px] text-gray-400 mt-1">
                                            <strong className="font-semibold">**negrito**</strong>, <em>*itálico*</em> e linhas começando com <code className="font-mono">-</code> viram lista.
                                        </p>
                                    </>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Anexo (opcional)</label>
                                <div className="flex gap-2">
                                    <input
                                        type="url"
                                        value={form.url_anexo}
                                        onChange={(e) => setForm({ ...form, url_anexo: e.target.value })}
                                        placeholder="Cole um link ou envie um arquivo"
                                        className="flex-1 min-w-0 px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-900 dark:text-white rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                    />
                                    <label className={`shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${enviandoAnexo ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-wait' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 cursor-pointer'}`}>
                                        {enviandoAnexo ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                                        {enviandoAnexo ? 'Enviando...' : 'Enviar arquivo'}
                                        <input
                                            type="file"
                                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                            className="hidden"
                                            disabled={enviandoAnexo}
                                            onChange={enviarAnexo}
                                        />
                                    </label>
                                </div>
                                {form.url_anexo && (
                                    <p className="text-xs text-gray-400 mt-1.5 truncate">
                                        Anexo atual:{' '}
                                        <a href={form.url_anexo} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                                            {form.url_anexo}
                                        </a>
                                    </p>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Vigência de</label>
                                    <input
                                        type="datetime-local"
                                        value={form.data_inicio}
                                        onChange={(e) => setForm({ ...form, data_inicio: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-900 dark:text-white rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Vigência até</label>
                                    <input
                                        type="datetime-local"
                                        value={form.data_fim}
                                        onChange={(e) => setForm({ ...form, data_fim: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-900 dark:text-white rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                    />
                                </div>
                            </div>
                            <div>
                                <label htmlFor="aviso-publico" className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Quem vê este aviso</label>
                                <select
                                    id="aviso-publico"
                                    value={form.publico}
                                    onChange={(e) => setForm({ ...form, publico: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-900 dark:text-white rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                >
                                    {PUBLICOS.map((p) => <option key={p.valor} value={p.valor}>{p.rotulo}</option>)}
                                </select>
                                <p className="text-[11px] text-gray-400 mt-1">Clientes Gran veem o portal com a marca da Gran e só os avisos marcados para eles ou para todos.</p>
                            </div>
                            <label className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
                                <input
                                    type="checkbox"
                                    checked={form.bloqueante}
                                    onChange={(e) => setForm({ ...form, bloqueante: e.target.checked })}
                                    className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary/30"
                                />
                                Exigir confirmação antes de continuar (bloqueante)
                            </label>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-900/50 px-6 py-4 flex justify-end gap-2">
                            <button
                                onClick={() => salvar('rascunho')}
                                disabled={salvando}
                                className="px-4 py-2.5 rounded-lg text-sm font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 ring-1 ring-inset ring-gray-300 dark:ring-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                            >
                                Salvar rascunho
                            </button>
                            <button
                                onClick={() => { if (formValido()) setConfirmarPublicar({}); }}
                                disabled={salvando}
                                className="px-4 py-2.5 rounded-lg text-sm font-bold text-white bg-primary hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
                            >
                                {salvando && <Loader2 size={14} className="animate-spin" />}
                                Publicar
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            <ConfirmationModal
                isOpen={!!confirmarPublicar}
                onClose={() => setConfirmarPublicar(null)}
                onConfirm={() => (confirmarPublicar?.id ? publicarRascunho(confirmarPublicar.id) : salvar('publicado'))}
                title="Publicar este aviso?"
                description="Ele vai aparecer para todos os consumidores ativos no próximo login. Se marcado como bloqueante, eles não conseguem acessar o portal sem confirmar."
                confirmText="Publicar"
                variant="warning"
                isLoading={salvando}
            />

            <ConfirmationModal
                isOpen={!!confirmarDespublicar}
                onClose={() => setConfirmarDespublicar(null)}
                onConfirm={() => confirmarDespublicar && despublicar(confirmarDespublicar)}
                title="Despublicar este aviso?"
                description="Ele sai do Portal do Consumidor na hora e volta para rascunho, pronto para você corrigir e publicar de novo. Quem já confirmou continua confirmado e não vai ver o aviso outra vez."
                confirmText="Despublicar"
                variant="warning"
            />

            <ConfirmationModal
                isOpen={!!confirmarArquivar}
                onClose={() => setConfirmarArquivar(null)}
                onConfirm={() => confirmarArquivar && arquivar(confirmarArquivar)}
                title="Arquivar este aviso?"
                description={confirmarArquivar?.status === 'publicado'
                    ? 'Ele deixa de aparecer para os consumidores imediatamente e sai da lista de avisos ativos. Esta ação não apaga o histórico de confirmações já registradas.'
                    : 'O rascunho sai da lista de avisos ativos. Ele não pode mais ser editado nem publicado depois disso.'}
                confirmText="Arquivar"
                variant="danger"
            />
        </div>
    );
};

export default AvisosConsumidorView;
